/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Serialize the token-body CL-F05 preview and grant acceptance operations.
 * @state-owner request-local transport
 * @allowed-imports API client public type
 * @forbidden React; routes; token persistence; direct fetch
 * @public-api classroom join transport
 * @side-effects authenticated grant preview and acceptance requests
 * @tests api/classroom-join.test.ts and components/ClassroomJoinPage.test.tsx
 * @naming-hook request functions are named after the classroom-grant action they perform
 * @naming-path classroom-grant paths are lowercase, hyphenated, and versioned consistently
 * @naming-body request bodies use camelCase and never repeat caller or resource identity
 */

import type { VispoApiClient } from '@vispo/api-client';

export interface ClassroomGrantPreview {
  grantId: string;
  kind: 'invitation' | 'student_join_link';
  grantRole: 'teacher' | 'student';
  classroomName: string;
  expiresAt: string;
}

export interface ClassroomGrantAcceptance {
  membershipId: string;
  classroomId: string;
  role: 'owner' | 'teacher' | 'student';
}

export function previewClassroomGrantRequest(apiClient: VispoApiClient, token: string) {
  return apiClient.request<ClassroomGrantPreview>('/api/v1/classroom-grants/preview', {
    method: 'POST',
    body: { token },
  });
}

export function acceptClassroomGrantRequest(
  apiClient: VispoApiClient,
  grant: Pick<ClassroomGrantPreview, 'grantId' | 'kind'>,
  token: string,
) {
  const path =
    grant.kind === 'invitation'
      ? `/api/v1/classroom-invitations/${encodeURIComponent(grant.grantId)}/accept`
      : `/api/v1/student-join-links/${encodeURIComponent(grant.grantId)}/accept`;
  return apiClient.request<ClassroomGrantAcceptance>(path, {
    method: 'POST',
    body: {
      token,
      idempotencyKey:
        globalThis.crypto?.randomUUID?.() ?? `agent-b-cl-f05-${Date.now().toString(36)}`,
    },
  });
}

export function declineClassroomInvitationRequest(
  apiClient: VispoApiClient,
  invitationId: string,
  token: string,
) {
  return apiClient.request<{ status: 'declined' }>(
    `/api/v1/classroom-invitations/${encodeURIComponent(invitationId)}/decline`,
    {
      method: 'POST',
      body: {
        token,
        idempotencyKey:
          globalThis.crypto?.randomUUID?.() ?? `agent-b-cl-f05-${Date.now().toString(36)}`,
      },
    },
  );
}
